import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConfigProvider, useConfig, isCalendarHostname } from './context/ConfigContext';
import CalendarApp from './pages/Calendar/CalendarApp';
import { ThemeProvider } from './context/ThemeContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import MarketingNavbar from './components/marketing/MarketingNavbar';
import MarketingFooter from './components/marketing/MarketingFooter';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import Home from './pages/Home';
import Properties from './pages/Properties';
import MarketingHome from './pages/marketing/MarketingHome';
import MarketingFeatures from './pages/marketing/MarketingFeatures';
import MarketingPricing from './pages/marketing/MarketingPricing';
import MarketingContact from './pages/marketing/MarketingContact';
import MarketingDemo from './pages/marketing/MarketingDemo';
import MarketingSignup from './pages/marketing/MarketingSignup';
import PropertyDetail from './pages/PropertyDetail';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AdminForgotPassword from './pages/AdminForgotPassword';
import AdminResetPassword from './pages/AdminResetPassword';
import SetupAccount from './pages/SetupAccount';
import Dashboard from './pages/Dashboard';
import LoanDetail from './pages/LoanDetail';
import PaymentHistory from './pages/PaymentHistory';
import SoldProperties from './pages/SoldProperties';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import PropertyManagement from './pages/PropertyManagement';
import CustomerManagement from './pages/CustomerManagement';
import AdminLoans from './pages/AdminLoans';
import PaymentTracking from './pages/PaymentTracking';
import DefaultedLoansReport from './pages/DefaultedLoansReport';
import AdminReports from './pages/AdminReports';
import TaxSummary from './pages/TaxSummary';
import StateManagement from './pages/StateManagement';
import AccountSettings from './pages/AccountSettings';
import ImportLoan from './pages/ImportLoan';
import CreateCustomLoan from './pages/CreateCustomLoan';
import EbayListingGenerator from './pages/EbayListingGenerator';
import EbayWinnerForm from './pages/EbayWinnerForm';
import EbaySubmissions from './pages/EbaySubmissions';
import EbayFormSettings from './pages/Admin/EbayFormSettings';
import PropertySources from './pages/PropertySources';
import AuctionCalendar from './pages/AuctionCalendar';
import MasterAdminCalendar from './pages/MasterAdmin/MasterAdminCalendar';
import BrandingSettings from './pages/BrandingSettings';
import BusinessSettings from './pages/BusinessSettings';
import FeatureSettings from './pages/FeatureSettings';
import FaqSettings from './pages/FaqSettings';
import PageContentSettings from './pages/PageContentSettings';
import PropertiesHub from './pages/Admin/PropertiesHub';
import CustomersLoansHub from './pages/Admin/CustomersLoansHub';
import ReportsHub from './pages/Admin/ReportsHub';
import SettingsHub from './pages/Admin/SettingsHub';
import ToolsHub from './pages/Admin/ToolsHub';
import AccountHub from './pages/Admin/AccountHub';
import BackupExport from './pages/Admin/BackupExport';
import CustomDomainSettings from './pages/Admin/CustomDomainSettings';
import EmailTemplatesSettings from './pages/Admin/EmailTemplatesSettings';
import SquareSettings from './pages/Admin/SquareSettings';
import UserManagement from './pages/Admin/UserManagement';
import AdminHelp from './pages/Admin/AdminHelp';
import AdminBilling from './pages/Admin/AdminBilling';
import AdminCalendar from './pages/Admin/AdminCalendar';
import TierSelection from './pages/TierSelection';
import SignupPayment from './pages/SignupPayment';
import AboutUs from './pages/AboutUs';
import ContactUs from './pages/ContactUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import MasterAdminLogin from './pages/MasterAdmin/MasterAdminLogin';
import MasterAdminDashboard from './pages/MasterAdmin/MasterAdminDashboard';
import MasterAdminTenants from './pages/MasterAdmin/MasterAdminTenants';
import MasterAdminTenantDetail from './pages/MasterAdmin/MasterAdminTenantDetail';
import MasterAdminBilling from './pages/MasterAdmin/MasterAdminBilling';
import MasterAdminFinancials from './pages/MasterAdmin/MasterAdminFinancials';
import MasterAdminEmails from './pages/MasterAdmin/MasterAdminEmails';
import MasterAdminSettings from './pages/MasterAdmin/MasterAdminSettings';
import AccountSuspended from './pages/AccountSuspended';
import { ToastProvider } from './context/ToastContext';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();
  
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
}

// Protected route component
// Protected route component
function ProtectedRoute({ children }) {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" />;
}

function AppContent() {
  const location = useLocation();
  const {
    getCompanyName,
    isMarketingSite,
    currentTenant,
    loading: configLoading,
    error: configError
  } = useConfig();
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isMasterAdminRoute = location.pathname.startsWith('/master-admin');

  // Marketing routes - on deedstack.com, ALL non-admin pages use marketing nav
  // This ensures pages like /privacy-policy, /terms-of-service show marketing nav, not tenant nav
  const isMarketingRoute = isMarketingSite && !isAdminRoute && !isMasterAdminRoute;

  // Set document title based on site type
  useEffect(() => {
    // Don't set title while config is still loading
    if (configLoading) {
      return;
    }

    if (isMarketingSite) {
      document.title = 'DeedStack™ - Land Financing Platform';
    } else if (currentTenant) {
      // Use tenant's company name from ConfigContext
      document.title = `${currentTenant.companyName} - Land Financing`;
    } else {
      // For admin users, check localStorage as fallback
      const adminUser = localStorage.getItem('adminUser');
      if (adminUser) {
        try {
          const parsed = JSON.parse(adminUser);
          if (parsed.tenantName) {
            document.title = `${parsed.tenantName} - Land Financing`;
            return;
          }
        } catch (e) {
          // Fall through to default
        }
      }
      // Fall back to config-based company name
      const companyName = getCompanyName();
      document.title = `${companyName} - Land Financing`;
    }
  }, [configLoading, isMarketingSite, currentTenant, getCompanyName]);

  // Note: Favicon and meta tags are now updated by ConfigContext immediately
  // when tenant data loads (before React state updates) via updateMetaTags utility

  // The Auction Calendar renders alone: no tenant navigation, no marketing
  // navigation, and it does not wait on the tenant config it never reads.
  //
  // Placed after every hook above on purpose. An early return before them would
  // change the hook order between renders, which the rules of hooks forbid and
  // the build enforces.
  if (isCalendarHostname(window.location.hostname)) {
    return <CalendarApp />;
  }

  // Show loading state while config is being fetched
  if (configLoading) {
    return (
      // This screen renders BEFORE the tenant config resolves, which makes it the
      // one most able to produce the white flash dark mode is supposed to remove.
      // The pre-paint script has already set data-theme by the time this mounts,
      // so these variables resolve to the right palette on the first frame.
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--color-background)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid var(--color-border)',
            borderTopColor: 'var(--color-brand-text)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }} />
          <p style={{ color: 'var(--color-text-light)', margin: 0 }}>Loading...</p>
          <style>{`
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Show error state if tenant not found (for subdomain sites)
  if (configError && !isMarketingSite) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: 'var(--color-surface-sunken)'
      }}>
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: 'var(--color-surface)',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          maxWidth: '400px'
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🏠</div>
          <h1 style={{ margin: '0 0 8px 0', color: 'var(--color-text)', fontSize: '24px' }}>Site Not Found</h1>
          <p style={{ color: 'var(--color-text-light)', margin: '0 0 24px 0' }}>{configError}</p>
          <a
            href="https://deedstack.com"
            style={{
              display: 'inline-block',
              padding: '12px 24px',
              backgroundColor: '#1a365d',
              color: 'white',
              textDecoration: 'none',
              borderRadius: '6px',
              fontWeight: '600'
            }}
          >
            Go to DeedStack.com
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <ScrollToTop />
      {isMarketingRoute && <MarketingNavbar />}
      {!isAdminRoute && !isMasterAdminRoute && !isMarketingRoute && <Navbar />}
      <Routes>
            {/* Marketing Routes - only on deedstack.com */}
            <Route path="/" element={isMarketingSite ? <MarketingHome /> : <Home />} />
            <Route path="/features" element={<MarketingFeatures />} />
            <Route path="/pricing" element={<MarketingPricing />} />
            <Route path="/demo" element={<MarketingDemo />} />
            <Route path="/signup" element={<MarketingSignup />} />
            <Route path="/contact" element={isMarketingSite ? <MarketingContact /> : <ContactUs />} />

            {/* Platform Home (legacy /home route) */}
            <Route path="/home" element={<Home />} />
            <Route path="/properties" element={<Properties />} />
            <Route path="/sold-properties" element={<SoldProperties />} />
            <Route path="/properties/:id" element={<PropertyDetail />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
	    <Route path="/about" element={<AboutUs />} />
            <Route path="/ebay-winner" element={<EbayWinnerForm />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/terms-of-service" element={<Navigate to="/terms" replace />} /> 
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/loans/:id" 
              element={
                <ProtectedRoute>
                  <LoanDetail />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/loans/:id/payments" 
              element={
                <ProtectedRoute>
                  <PaymentHistory />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/account-settings" 
              element={
                <ProtectedRoute>
                  <AccountSettings />
                </ProtectedRoute>
              } 
            />
            {/* Admin Routes */}
            <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/forgot-password" element={<AdminForgotPassword />} />
            <Route path="/admin/reset-password" element={<AdminResetPassword />} />
            <Route path="/setup-account" element={<SetupAccount />} />
            <Route path="/admin/suspended" element={<AdminProtectedRoute allowSuspended={true}><AccountSuspended /></AdminProtectedRoute>} />
            <Route path="/admin/dashboard" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
            <Route path="/admin/properties-hub" element={<AdminProtectedRoute><PropertiesHub /></AdminProtectedRoute>} />
            <Route path="/admin/customers-loans-hub" element={<AdminProtectedRoute><CustomersLoansHub /></AdminProtectedRoute>} />
            <Route path="/admin/reports-hub" element={<AdminProtectedRoute><ReportsHub /></AdminProtectedRoute>} />
            <Route path="/admin/settings-hub" element={<AdminProtectedRoute><SettingsHub /></AdminProtectedRoute>} />
            <Route path="/admin/tools-hub" element={<AdminProtectedRoute><ToolsHub /></AdminProtectedRoute>} />
            <Route path="/admin/account-hub" element={<AdminProtectedRoute><AccountHub /></AdminProtectedRoute>} />
            <Route path="/admin/properties" element={<AdminProtectedRoute><PropertyManagement /></AdminProtectedRoute>} />
            <Route path="/admin/properties/:propertyId" element={<AdminProtectedRoute><PropertyManagement /></AdminProtectedRoute>} />
            <Route path="/admin/customers" element={<AdminProtectedRoute><CustomerManagement /></AdminProtectedRoute>} />
            <Route path="/admin/loans" element={<AdminProtectedRoute><AdminLoans /></AdminProtectedRoute>} />
            <Route path="/admin/payments" element={<AdminProtectedRoute><PaymentTracking /></AdminProtectedRoute>} />
            <Route path="/admin/loans/defaulted" element={<AdminProtectedRoute><DefaultedLoansReport /></AdminProtectedRoute>} />
            <Route path="/admin/reports" element={<AdminProtectedRoute><AdminReports /></AdminProtectedRoute>} />
            <Route path="/admin/tax-summary" element={<AdminProtectedRoute><TaxSummary /></AdminProtectedRoute>} />
            <Route path="/admin/states" element={<AdminProtectedRoute><StateManagement /></AdminProtectedRoute>} />
            <Route path="/admin/loans/import" element={<AdminProtectedRoute><ImportLoan /></AdminProtectedRoute>} />
            <Route path="/admin/loans/create" element={<AdminProtectedRoute><CreateCustomLoan /></AdminProtectedRoute>} />
            <Route path="/admin/ebay-listing-generator" element={<AdminProtectedRoute><EbayListingGenerator /></AdminProtectedRoute>} />
            <Route path="/admin/ebay-submissions" element={<AdminProtectedRoute><EbaySubmissions /></AdminProtectedRoute>} />
            <Route path="/admin/ebay-form-settings" element={<AdminProtectedRoute><EbayFormSettings /></AdminProtectedRoute>} />
            <Route path="/admin/property-sources" element={<AdminProtectedRoute><PropertySources /></AdminProtectedRoute>} />
            <Route path="/admin/auction-calendar" element={<AdminProtectedRoute><AuctionCalendar /></AdminProtectedRoute>} />
          {/*
            The PLATFORM Auction Calendar, not the tenant's own auction list on
            the line above. Splat route because AdminCalendar declares its own
            nested Routes for the state, county and month screens it shares with
            the subscriber app. Entitlement is decided by the server, not here:
            an unentitled tenant reaching this URL gets the upgrade page.
          */}
          <Route path="/admin/calendar/*" element={<AdminProtectedRoute><AdminCalendar /></AdminProtectedRoute>} />
            <Route path="/admin/settings/branding" element={<AdminProtectedRoute><BrandingSettings /></AdminProtectedRoute>} />
            <Route path="/admin/settings/business" element={<AdminProtectedRoute><BusinessSettings /></AdminProtectedRoute>} />
            <Route path="/admin/settings/features" element={<AdminProtectedRoute><FeatureSettings /></AdminProtectedRoute>} />
            <Route path="/admin/settings/faq" element={<AdminProtectedRoute><FaqSettings /></AdminProtectedRoute>} />
            <Route path="/admin/settings/square" element={<AdminProtectedRoute><SquareSettings /></AdminProtectedRoute>} />
            <Route path="/admin/settings/page-content" element={<AdminProtectedRoute><PageContentSettings /></AdminProtectedRoute>} />
            <Route path="/admin/settings/custom-domain" element={<AdminProtectedRoute><CustomDomainSettings /></AdminProtectedRoute>} />
            <Route path="/admin/settings/email-templates" element={<AdminProtectedRoute><EmailTemplatesSettings /></AdminProtectedRoute>} />
            <Route path="/admin/settings/users" element={<AdminProtectedRoute><UserManagement /></AdminProtectedRoute>} />
            <Route path="/admin/backup-export" element={<AdminProtectedRoute><BackupExport /></AdminProtectedRoute>} />
            <Route path="/admin/help" element={<AdminProtectedRoute><AdminHelp /></AdminProtectedRoute>} />
            <Route path="/admin/billing" element={<AdminProtectedRoute allowSuspended={true}><AdminBilling /></AdminProtectedRoute>} />
            <Route path="/signup/select-tier" element={<AdminProtectedRoute><TierSelection /></AdminProtectedRoute>} />
            <Route path="/signup/payment" element={<AdminProtectedRoute><SignupPayment /></AdminProtectedRoute>} />

            {/* Master Admin Routes - Only accessible from main domain (deedstack.com) */}
            {isMarketingSite ? (
              <>
                <Route path="/master-admin" element={<Navigate to="/master-admin/dashboard" replace />} />
                <Route path="/master-admin/login" element={<MasterAdminLogin />} />
                <Route path="/master-admin/dashboard" element={<MasterAdminDashboard />} />
                <Route path="/master-admin/tenants" element={<MasterAdminTenants />} />
                <Route path="/master-admin/tenants/:id" element={<MasterAdminTenantDetail />} />
                <Route path="/master-admin/calendar" element={<MasterAdminCalendar />} />
                <Route path="/master-admin/billing" element={<MasterAdminBilling />} />
                <Route path="/master-admin/financials" element={<MasterAdminFinancials />} />
                <Route path="/master-admin/emails" element={<MasterAdminEmails />} />
                <Route path="/master-admin/settings" element={<MasterAdminSettings />} />
              </>
            ) : (
              /* On tenant subdomains, redirect master-admin to main domain */
              <Route path="/master-admin/*" element={
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100vh',
                  backgroundColor: 'var(--color-surface-sunken)'
                }}>
                  <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    backgroundColor: 'var(--color-surface)',
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                    maxWidth: '400px'
                  }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🔐</div>
                    <h1 style={{ margin: '0 0 8px 0', color: 'var(--color-text)', fontSize: '24px' }}>Access Restricted</h1>
                    <p style={{ color: 'var(--color-text-light)', margin: '0 0 24px 0' }}>
                      Master Admin is only accessible from the main DeedStack domain.
                    </p>
                    <a
                      href="https://deedstack.com/master-admin/login"
                      style={{
                        display: 'inline-block',
                        padding: '12px 24px',
                        backgroundColor: '#1a365d',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '6px',
                        fontWeight: '600'
                      }}
                    >
                      Go to Master Admin
                    </a>
                  </div>
                </div>
              } />
            )}
          </Routes>
	  {isMarketingRoute && <MarketingFooter />}
      {!isAdminRoute && !isMasterAdminRoute && !isMarketingRoute && <Footer />}
        </div>
  );
}

/**
 * Hands the tenant's configured default theme to ThemeProvider.
 *
 * It exists as a separate component because ThemeProvider deliberately knows
 * nothing about ConfigContext: the theme has to work on the marketing site and
 * in master admin, neither of which has a tenant. This is the one seam where the
 * two meet, and it sits inside ConfigProvider so getDefaultTheme() is available.
 *
 * The default only ever seeds a visitor with nothing stored. See the comment at
 * the top of context/ThemeContext.js for why it cannot be applied before the
 * first paint on a static host.
 */
function ThemeBridge({ children }) {
  const { getDefaultTheme } = useConfig();

  return (
    <ThemeProvider tenantDefault={getDefaultTheme()}>
      {children}
    </ThemeProvider>
  );
}

function App() {
  return (
    <Router>
      <ConfigProvider>
        <ToastProvider>
          <AuthProvider>
            <ThemeBridge>
              <AppContent />
            </ThemeBridge>
          </AuthProvider>
        </ToastProvider>
      </ConfigProvider>
    </Router>
  );
}

export default App;